import type { ReactNode } from 'react';
import { Button } from './Button';
import { cx } from '@/lib/cx';

export interface ViewToggleOption<T extends string> {
  value: T;
  label: ReactNode;
}

export interface ViewToggleProps<T extends string> {
  /** Names the CHOICE ("View", "Section"), not the current answer. */
  label: string;
  value: T;
  options: readonly ViewToggleOption<T>[];
  onChange: (value: T) => void;
  className?: string;
  /**
   * `pills` (default) for a compact switch beside other controls; `underline`
   * for the sections of a page, drawn as one full-width line under its title.
   */
  variant?: 'pills' | 'underline';
}

/**
 * The view-pill strip — Table / Sheet, Schemes / Assignments, the settings
 * tabs — as one primitive instead of a container string copied page by page.
 *
 * ── THE THINGS THAT ARE NOT DECORATION ───────────────────────────────────────
 *
 * 1. **`role="group"` + `aria-pressed`, NOT `role="tab"`.** A real `tablist`
 *    promises arrow-key roving between panels that share one tabstop, which
 *    these plain buttons do not implement. Every pill stays an ordinary
 *    `<button>`, in the tab order, announced with the keys it really takes.
 *
 * 2. **The selected pill is neutral, never the accent.** It is the raised
 *    `secondary` tab inside the sunken track — the same rendering
 *    `SegmentedControl` uses, so the two idioms read as one. The accent is
 *    reserved for primary actions, the active nav row and focus.
 *
 * 3. ⚠️ **It WRAPS and it never carries `shrink-0`.** `flex-wrap` + `max-w-full`
 *    is what lets a four-pill strip fold onto two rows at 320px. With `shrink-0`
 *    the box never gets narrow enough for its own wrap to fire, and the last
 *    pill is clipped off the viewport by an ancestor — present in the DOM,
 *    reachable by nothing, and reported as a deleted feature (`/settings`,
 *    2026-09-19). Do not add it from a call site either.
 *
 * 4. **Re-selecting the current pill fires nothing**, so a caller may treat
 *    every `onChange` as a real change.
 */
export function ViewToggle<T extends string>({
  label,
  value,
  options,
  onChange,
  className,
  variant = 'pills',
}: ViewToggleProps<T>) {
  if (variant === 'underline') {
    /*
     * One full-width line under a page title: the active section is marked by
     * weight, colour AND a rule beneath it, so colour is never the only signal.
     * Below `sm` the row scrolls sideways inside itself rather than wrapping,
     * so it stays one line. Same semantics as the pills: a labelled group of
     * pressed buttons, and re-selecting the current one fires nothing.
     */
    return (
      <div
        role="group"
        aria-label={label}
        className={cx(
          'flex max-w-full items-end gap-1 overflow-x-auto border-b border-border',
          className,
        )}
      >
        {options.map((option) => {
          const selected = option.value === value;
          return (
            <button
              key={option.value}
              type="button"
              aria-pressed={selected}
              onClick={() => {
                if (!selected) onChange(option.value);
              }}
              className={cx(
                '-mb-px inline-flex h-10 shrink-0 items-center whitespace-nowrap border-b-2 px-3 text-sm transition-colors duration-150 ease-out motion-reduce:transition-none max-sm:h-11',
                selected
                  ? 'border-accent-brand font-semibold text-fg'
                  : 'border-transparent font-medium text-fg-muted hover:border-hairline-strong hover:text-fg',
              )}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    );
  }
  return (
    <div
      role="group"
      aria-label={label}
      className={cx(
        'inline-flex max-w-full flex-wrap items-center gap-0.5 rounded-md border border-border bg-surface-sunken p-0.5',
        className,
      )}
    >
      {options.map((option) => {
        const selected = option.value === value;
        return (
          <Button
            key={option.value}
            type="button"
            size="sm"
            variant={selected ? 'secondary' : 'ghost'}
            aria-pressed={selected}
            onClick={() => {
              if (!selected) onChange(option.value);
            }}
          >
            {option.label}
          </Button>
        );
      })}
    </div>
  );
}
